import { useCallback, useEffect, useState } from "react";

import { Icon } from "./Icon.js";
import { ApiError } from "../api/client.js";
import { useI18n } from "../i18n.js";
import { useSession } from "../session.js";

/**
 * Gli account, come li disegnano `SettingsAdminTab.qml` e `Users.qml`.
 *
 * Le due schermate dell'app desktop mostrano la stessa cosa — le due schede
 * riassuntive e l'elenco a schede con modifica, cambio password e
 * cancellazione in linea — quindi qui e' un pezzo solo. Tenerne due copie
 * vorrebbe dire che una delle due, prima o poi, resta indietro.
 *
 * Due regole che vengono dal `.qml` e non sono estetiche:
 *
 *  - **sulla propria riga non c'e' ne' modifica ne' cancellazione**.
 *    Cancellarsi o abbassarsi di livello da soli, su un server di sala
 *    controllo, puo' voler dire nessuno piu' dentro.
 *  - il **livello segue il ruolo**: sono la stessa scala, e lasciarli
 *    scegliere separatamente produce un "supervisore di livello 1" che non
 *    puo' fare niente di quello che il suo nome promette.
 */

export interface UserRow {
  id: string;
  username: string;
  displayName: string | null;
  role: string | null;
  level: number;
  isActive?: number;
  lastLoginAt?: string | null;
}

export const ROLES = ["viewer", "operator", "supervisor", "admin", "superadmin"] as const;
export type Role = (typeof ROLES)[number];

/** Il livello di un ruolo. Stessa tabella del `.qml`. */
export function roleLevel(role: string): number {
  const i = ROLES.indexOf(role as Role);
  return i < 0 ? 1 : i + 1;
}

const ROLE_KEYS = [
  "roles.viewer",
  "roles.operator",
  "roles.supervisor",
  "roles.admin",
  "roles.superadmin",
];
/**
 * I cinque colori dei livelli, copiati da `SettingsAdminTab.qml`.
 *
 * **I due `.qml` non concordano fra loro**: la scheda AMMINISTRAZIONE scrive
 * questa lista, `Users.qml` mette `Theme.primary` (bianco) al posto
 * dell'azzurro del terzo. Gli altri quattro coincidono con i colori del tema,
 * quindi l'azzurro sembra voluto e il bianco una svista — ma qui c'e' un
 * pezzo solo per le due schermate, e questa lista e' quella della scheda che
 * si apre piu' spesso. Da confermare guardando l'app desktop.
 */
const ROLE_COLORS = ["#a3a3a3", "#22c55e", "#3b82f6", "#f59e0b", "#ef4444"];

/** Carica gli account e li tiene aggiornati. Torna anche l'errore, perche' un
 *  403 su questa lista non e' un guasto: e' una risposta. */
export function useUsers(pollMs = 0) {
  const { api } = useSession();
  const [users, setUsers] = useState<UserRow[]>([]);
  const [denied, setDenied] = useState(false);
  const [error, setError] = useState("");

  const reload = useCallback(async () => {
    try {
      setUsers(await api.call<UserRow[]>("users.list"));
      setDenied(false);
      setError("");
    } catch (e) {
      if (e instanceof ApiError && e.isForbidden) setDenied(true);
      else setError(e instanceof ApiError ? e.message : String(e));
    }
  }, [api]);

  useEffect(() => {
    void reload();
    if (pollMs <= 0) return undefined;
    const timer = setInterval(() => void reload(), pollMs);
    return () => clearInterval(timer);
  }, [reload, pollMs]);

  return { users, denied, error, setError, reload };
}

/** Le due schede in cima: quanti account ci sono e cosa vuol dire ogni livello. */
export function UserKpis({ count }: { count: number }) {
  const { t } = useI18n();
  return (
    <div className="ukpis">
      <div className="ukpi">
        <span className="ukpi__icon">
          <Icon name="users" size={22} />
        </span>
        <div>
          <div className="ukpi__value">{count}</div>
          <div className="ukpi__label">{t("settings.activeUsers")}</div>
        </div>
      </div>
      <div className="ukpi ukpi--legend">
        <div className="ukpi__label">{t("settings.levelLegend")}</div>
        <div className="ulegend">
          {ROLE_KEYS.map((k, i) => (
            <span key={k} className="ulevel" style={{ borderColor: ROLE_COLORS[i], color: ROLE_COLORS[i] }}>
              Lv.{i + 1} {t(k)}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

export function UserList({
  users,
  onChanged,
  onError,
}: {
  users: UserRow[];
  onChanged: () => void;
  onError: (message: string) => void;
}) {
  const { t } = useI18n();
  const { api, phase } = useSession();
  const me = phase.kind === "authenticated" ? phase.user : undefined;

  const [editing, setEditing] = useState("");
  const [editName, setEditName] = useState("");
  const [editRole, setEditRole] = useState<Role>("viewer");
  const [pwdFor, setPwdFor] = useState("");
  const [pwd, setPwd] = useState("");

  const run = async (route: "users.update" | "users.remove" | "users.password", body: unknown) => {
    try {
      await api.call(route, body);
      onError("");
      onChanged();
      return true;
    } catch (e) {
      onError(e instanceof ApiError ? e.message : String(e));
      return false;
    }
  };

  return (
    <div className="ulist">
      {users.map((u) => {
        const lvl = Math.max(0, (u.level || 1) - 1);
        const self = u.id === me?.id;
        return (
          <div className="ucard" key={u.id}>
            <div className="ucard__row">
              <span className="ucard__avatar">
                {(u.displayName || u.username || "?").slice(0, 2).toUpperCase()}
              </span>
              <div className="ucard__id">
                <b>{u.displayName || u.username || "—"}</b>
                <span className="muted small">@{u.username}</span>
              </div>
              <div className="ucard__role" style={{ color: ROLE_COLORS[lvl] }}>
                <b>{t(ROLE_KEYS[lvl] ?? "roles.viewer")}</b>
                <span>Lv.{u.level || 1}</span>
              </div>

              {editing !== u.id && pwdFor !== u.id && (
                <>
                  {/* Modificare o cancellare se stessi puo' lasciare il
                      sistema senza nessuno che ci possa entrare. */}
                  {!self && (
                    <button
                      className="ucard__act"
                      title={t("settings.save")}
                      onClick={() => {
                        setEditing(u.id);
                        setEditName(u.displayName ?? "");
                        setEditRole((u.role as Role) ?? "viewer");
                      }}
                    >
                      <Icon name="pencil" size={14} />
                    </button>
                  )}
                  <button
                    className="ucard__act"
                    title={t("users.newPassword")}
                    onClick={() => {
                      setPwdFor(u.id);
                      setPwd("");
                    }}
                  >
                    <Icon name="shield" size={14} />
                  </button>
                  {!self && (
                    <button
                      className="ucard__act ucard__act--danger"
                      title={t("settings.deactivate")}
                      onClick={() => void run("users.remove", { id: u.id })}
                    >
                      <Icon name="trash-2" size={14} />
                    </button>
                  )}
                </>
              )}
            </div>

            {editing === u.id && (
              <div className="ucard__form">
                <span className="ametric__label">{t("users.editUser")}</span>
                <input
                  value={editName}
                  placeholder={t("users.displayName")}
                  onChange={(e) => setEditName(e.target.value)}
                />
                <select value={editRole} onChange={(e) => setEditRole(e.target.value as Role)}>
                  {ROLES.map((r) => (
                    <option key={r} value={r}>
                      {t(ROLE_KEYS[roleLevel(r) - 1] ?? r)}
                    </option>
                  ))}
                </select>
                <div className="ucard__buttons">
                  <button onClick={() => setEditing("")}>{t("settings.cancel")}</button>
                  <button
                    className="btn--primary"
                    onClick={() =>
                      void run("users.update", {
                        id: u.id,
                        displayName: editName,
                        role: editRole,
                        level: roleLevel(editRole),
                      }).then((ok) => ok && setEditing(""))
                    }
                  >
                    {t("settings.save")}
                  </button>
                </div>
              </div>
            )}

            {pwdFor === u.id && (
              <div className="ucard__form">
                <input
                  type="password"
                  value={pwd}
                  autoComplete="new-password"
                  placeholder={t("users.newPassword")}
                  onChange={(e) => setPwd(e.target.value)}
                />
                <p className="muted small">{t("users.minChars")}</p>
                <div className="ucard__buttons">
                  <button onClick={() => setPwdFor("")}>{t("settings.cancel")}</button>
                  <button
                    className="btn--primary"
                    disabled={pwd.length < 4}
                    onClick={() =>
                      void run("users.password", { id: u.id, password: pwd }).then((ok) => {
                        if (ok) {
                          setPwdFor("");
                          setPwd("");
                        }
                      })
                    }
                  >
                    {t("users.change")}
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Il riquadro "aggiungi utente", chiuso finche' non serve. */
export function AddUser({
  onCreated,
  onError,
}: {
  onCreated: () => void;
  onError: (message: string) => void;
}) {
  const { t } = useI18n();
  const { api } = useSession();
  const [open, setOpen] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<Role>("viewer");

  const ready = username.trim() && password && displayName.trim();

  const create = async () => {
    if (!ready) return;
    try {
      await api.call("users.create", {
        username: username.trim(),
        password,
        displayName: displayName.trim(),
        role,
        level: roleLevel(role),
      });
      setOpen(false);
      setDisplayName("");
      setUsername("");
      setPassword("");
      setRole("viewer");
      onError("");
      onCreated();
    } catch (e) {
      onError(e instanceof ApiError ? e.message : String(e));
    }
  };

  return (
    <div className="ucard uadd">
      <button className="uadd__head" onClick={() => setOpen(!open)}>
        <Icon name="plus" size={16} />
        {t("users.addUser")}
      </button>
      {open && (
        <div className="ucard__form">
          <span className="ametric__label">{t("users.newUser")}</span>
          <input
            value={displayName}
            placeholder={t("users.displayName")}
            onChange={(e) => setDisplayName(e.target.value)}
          />
          <input
            value={username}
            placeholder={t("login.username")}
            onChange={(e) => setUsername(e.target.value)}
          />
          <input
            type="password"
            value={password}
            autoComplete="new-password"
            placeholder={t("login.password")}
            onChange={(e) => setPassword(e.target.value)}
          />
          <select value={role} onChange={(e) => setRole(e.target.value as Role)}>
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {t(ROLE_KEYS[roleLevel(r) - 1] ?? r)}
              </option>
            ))}
          </select>
          <button className="btn--primary uadd__go" disabled={!ready} onClick={() => void create()}>
            {t("settings.create")}
          </button>
        </div>
      )}
    </div>
  );
}
